import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  FaUsers,
  FaIdCard,
  FaMoneyBillWave,
  FaChartLine,
  FaUserTie,
  FaBullhorn,
  FaExclamationTriangle,
  FaArrowRight,
  FaWallet,
  FaMicrophone
} from 'react-icons/fa';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    raiseVoiceUsers: 0,
    pendingIdCards: 0,
    totalUsers: 0,
    totalAdvertiser: 0,
    totalReporter: 0,
    totalRevenue: 0,
    unverifiedReporterCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPendingIdCards = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/get/icards`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setStats((prevStats) => ({
        ...prevStats,
        pendingIdCards: Array.isArray(response.data.reportersIdCard) ? response.data.reportersIdCard.length : 0,
      }));
    } catch (err) {
      console.error('Failed to load pending ID cards', err);
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch all users
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/get/all/users/`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });

        //  fetch total revenue
        const revenueResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/get/revenew`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });

        // Fetch raise voice users
        const ryvResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/get/all/ryv/user`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });

        setStats(prevStats => ({
          ...prevStats,
          ...response.data.data,
          totalRevenue: revenueResponse.data?.data?.totalEarnings || 0,
          raiseVoiceUsers: ryvResponse.data?.data?.totalUsers || 0
        }));

      } catch (err) {
        setError('Failed to load dashboard stats');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    fetchPendingIdCards();
  }, []);

  const DashboardCard = ({ title, value, icon: Icon, color, onClick, subtext }) => (
    <div
      onClick={onClick}
      className="group bg-white p-3 md:p-6 rounded-xl md:rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer relative overflow-hidden"
    >
      <div className={`absolute top-0 right-0 w-16 md:w-24 h-16 md:h-24 transform translate-x-4 md:translate-x-8 -translate-y-4 md:-translate-y-8 rounded-full opacity-10 ${color} group-hover:scale-110 transition-transform duration-500`}></div>

      <div className="relative z-10 flex justify-between items-start mb-2 md:mb-4">
        <div className={`p-2 md:p-3 rounded-lg md:rounded-xl ${color.replace('bg-', 'bg-opacity-10 text-')}`}>
          <Icon className={`text-lg md:text-2xl ${color.replace('bg-', 'text-')}`} />
        </div>
        {onClick && (
          <div className="text-gray-400 group-hover:text-gray-600 transition-colors hidden md:block">
            <FaArrowRight className="w-4 h-4" />
          </div>
        )}
      </div>

      <div className="relative z-10">
        <h3 className="text-xl md:text-3xl font-black text-gray-800 mb-0 md:mb-1 font-sans">{value}</h3>
        <p className="text-gray-500 font-bold text-[10px] md:text-sm uppercase tracking-tight">{title}</p>
        {subtext && <p className="text-[8px] md:text-xs text-gray-400 mt-1 md:mt-2 line-clamp-1">{subtext}</p>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 md:p-8">

      {/* Welcome Section */}
      <div className="mb-6 md:mb-10 flex flex-col md:flex-row justify-between items-start md:items-end gap-3 md:gap-4 mt-[-10px] md:mt-0">
        <div>
          <h1 className="text-xl md:text-3xl font-black text-gray-800 mb-0.5 md:mb-2 uppercase tracking-tight">System Map</h1>
          <p className="text-gray-500 text-[10px] md:text-lg font-medium">Real-time platform status & user metrics.</p>
        </div>
        <div className="flex items-center gap-2 md:gap-3 w-full md:w-auto">
          <button className="flex-1 md:flex-none bg-white text-gray-600 px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-black border border-gray-200 hover:bg-gray-50 transition-colors shadow-sm uppercase tracking-widest">
            30 Days
          </button>
          <button className="flex-1 md:flex-none bg-blue-600 text-white px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-black hover:bg-blue-700 transition-colors shadow-sm shadow-blue-200 uppercase tracking-widest">
            Export
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-8">

          {/* User Statistics Section */}
          <div>
            <div className="flex items-center gap-2 mb-3 md:mb-4 px-1">
              <FaChartLine className="text-blue-500 w-3 h-3 md:w-4 md:h-4" />
              <h2 className="text-[10px] md:text-lg font-black text-gray-500 uppercase tracking-widest">User Ecosystem</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-6">
              <DashboardCard
                title="Total Users"
                value={stats.totalUsers}
                icon={FaUsers}
                color="bg-blue-500"
                onClick={() => navigate('/admin/alluser')}
                subtext="Registered accounts across all roles"
              />
              <DashboardCard
                title="Reporters"
                value={stats.reporters || stats.totalReporter || 0}
                icon={FaUserTie}
                color="bg-purple-500"
                onClick={() => navigate('/admin/totalreporters')}
                subtext="Active content contributors"
              />
              <DashboardCard
                title="Advertisers"
                value={stats.advertisers || stats.totalAdvertiser || 0}
                icon={FaBullhorn}
                color="bg-orange-500"
                onClick={() => navigate('/admin/totaladvertisers')}
                subtext="Business partners"
              />
              <DashboardCard
                title="Raise Voice"
                value={stats.raiseVoiceUsers}
                icon={FaBullhorn}
                color="bg-cyan-500"
                onClick={() => navigate('/admin/totalraisevoiceuser')}
                subtext="Community participants"
              />
              <DashboardCard
                title="Press Organizers"
                value=""
                icon={FaMicrophone}
                color="bg-green-500"
                onClick={() => navigate('/admin/press-conference/users')}
                subtext="Press conference accounts"
              />
            </div>
          </div>

          {/* Action Items Section */}
          <div>
            <div className="flex items-center gap-2 mb-3 md:mb-4 px-1">
              <FaExclamationTriangle className="text-amber-500 w-3 h-3 md:w-4 md:h-4" />
              <h2 className="text-[10px] md:text-lg font-black text-gray-500 uppercase tracking-widest">Critical Audits</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
              <DashboardCard
                title="Pending ID Cards"
                value={stats.pendingIdCards}
                icon={FaIdCard}
                color="bg-yellow-500"
                onClick={() => navigate('/admin/pendingidcards')}
                subtext="Requests awaiting your approval"
              />
              <DashboardCard
                title="Unverified Reporters"
                value={stats.unverifiedReporterCount}
                icon={FaUserTie}
                color="bg-red-500"
                onClick={() => navigate('/admin/totalunverifiedreporter')}
                subtext="Reporters needing verification"
              />
            </div>
          </div>

          {/* Financial Overview */}
          <div>
            <div className="flex items-center gap-2 mb-3 md:mb-4 px-1">
              <FaWallet className="text-emerald-500 w-3 h-3 md:w-4 md:h-4" />
              <h2 className="text-[10px] md:text-lg font-black text-gray-500 uppercase tracking-widest">Finance</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6">
              <div className="col-span-1 md:col-span-2 bg-gradient-to-br from-emerald-600 to-teal-800 rounded-xl md:rounded-2xl p-5 md:p-8 text-white shadow-lg relative overflow-hidden">
                <div className="absolute right-0 top-0 w-32 md:w-64 h-32 md:h-64 bg-white opacity-10 rounded-full transform translate-x-10 md:translate-x-20 -translate-y-10 md:-translate-y-20"></div>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-1 md:mb-2">
                    <div className="p-1.5 md:p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                      <FaMoneyBillWave className="text-sm md:text-xl" />
                    </div>
                    <span className="text-xs md:text-base font-black text-emerald-100 uppercase tracking-widest">Total Revenue</span>
                  </div>
                  <h3 className="text-3xl md:text-5xl font-black mb-3 md:mb-4 font-sans">
                    ₹{parseFloat(stats.totalRevenue || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </h3>
                  <div className="flex gap-4">
                    <button
                      onClick={() => navigate('/admin/payment/history')}
                      className="px-4 py-2 bg-white text-emerald-700 rounded-lg text-[10px] md:text-sm font-black uppercase tracking-widest hover:bg-emerald-50 transition-all shadow-md"
                    >
                      History
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default AdminDashboard;